// The Refund Policy section is anchored at #refunds because the Stripe
// business profile's refund policy URL field points to it directly.

import { Footer } from "@/components/layout/Footer";
import { Navigation } from "@/components/layout/Navigation";
import { Container } from "@/components/ui/Container";
import { useScrollToHash } from "@/lib/use-scroll-to-hash";
import { Link } from "react-router-dom";

const EFFECTIVE_DATE = "May 15, 2026";
const CONTACT_EMAIL = "info@mobeencenter.org";

export function TermsPage() {
  useScrollToHash();
  return (
    <>
      <Navigation
        entries={[
          { label: "Events", to: "/#events" },
          { label: "Donate", to: "/donate" },
        ]}
        homePath="/"
      />
      <main className="flex-1">
        <Container size="narrow" className="py-16 lg:py-24">
          <p className="eyebrow text-accent">Legal</p>
          <h1 className="display-title mt-3 text-[clamp(2rem,5vw,3.4rem)] leading-tight">
            Terms of Service
          </h1>
          <p className="mt-4 text-sm text-muted">Effective {EFFECTIVE_DATE}</p>

          <Section title="1. Acceptance">
            <p>
              These Terms of Service (the &ldquo;Terms&rdquo;) govern your use of the website at{" "}
              <code>mobeen.pages.dev</code> and any associated subdomains (the &ldquo;Site&rdquo;)
              operated by Mobeen Center (&ldquo;we,&rdquo; &ldquo;us&rdquo;). By using the Site,
              registering for an event, or making a donation, you agree to these Terms and to our{" "}
              <Link to="/privacy" className="text-accent underline-offset-4 hover:underline">
                Privacy Policy
              </Link>
              . If you do not agree, please do not use the Site.
            </p>
          </Section>

          <Section title="2. Who may use the Site">
            <p>
              The Site is open to the general public. If you are under 18, you may use the Site only
              under the supervision of a parent or guardian. Some features (such as registering a
              family for an event) are intended to be completed by adults on behalf of their
              household.
            </p>
          </Section>

          <Section title="3. Event registrations and submissions">
            <p>
              You agree to provide accurate information when you register for an event, sign up for
              a potluck, or submit a competition entry. You are responsible for keeping your edit
              passcode confidential; anyone with the passcode can modify or remove your submission.
              We may decline, edit, or remove submissions that are inaccurate, duplicated, abusive,
              or otherwise inconsistent with the spirit of the event.
            </p>
          </Section>

          <Section title="4. Donations">
            <p>
              Donations made through the Site support Mobeen Center&rsquo;s charitable activities,
              either the general fund or a specific event campaign you select. Mobeen Center is a
              501(c)(3) tax-exempt organization. To the extent provided by law, contributions are
              tax-deductible. No goods or services are provided in exchange for a donation unless we
              explicitly state otherwise at the point of giving.
            </p>
            <p className="mt-3">
              Payment processing is provided by Stripe, Inc. By donating you also agree to
              Stripe&rsquo;s applicable terms. We do not store your full card details.
            </p>
          </Section>

          <Section title="5. Refund Policy" id="refunds">
            <p>
              We accept donations in good faith and we want every donor to feel confident giving. If
              you donated by mistake, were charged the wrong amount, or want to withdraw a donation,
              contact us at <Mail /> as soon as possible.
            </p>
            <p className="mt-3">
              <strong>Standard window.</strong> We will issue a full refund for any donation when
              the request is received within 30 days of the charge date. Refunds requested after 30
              days will be considered case by case; we generally honor them when funds have not
              already been disbursed for charitable use.
            </p>
            <p className="mt-3">
              <strong>How refunds are processed.</strong> Refunds are returned to the original
              payment method through Stripe. The card processor&rsquo;s fees may not be returned to
              us, but we will refund the full donor amount regardless. Refunds typically appear on
              the donor&rsquo;s statement within 5 to 10 business days, depending on the issuing
              bank.
            </p>
            <p className="mt-3">
              <strong>Event campaign donations.</strong> If you contributed to a campaign tied to a
              specific event and the event is canceled or substantially changed, you may request
              either a full refund or a redirect of your donation to the general fund.
            </p>
            <p className="mt-3">
              <strong>Recurring donations.</strong> Recurring giving is not enabled at this time; if
              it becomes available, recurring donors will be able to cancel future charges from
              their receipt email or by contacting us at <Mail />.
            </p>
            <p className="mt-3">
              <strong>Tax acknowledgments.</strong> If a donation is refunded, the corresponding tax
              acknowledgment is void.
            </p>
          </Section>

          <Section title="6. Acceptable use">
            <p>You agree not to:</p>
            <ul className="mt-2 list-disc space-y-1 pl-5">
              <li>Submit false, fraudulent, or impersonating information.</li>
              <li>Interfere with the Site&rsquo;s operation or security.</li>
              <li>Attempt to access administrative areas without authorization.</li>
              <li>
                Use the Site to send unsolicited messages, harass others, or post unlawful content.
              </li>
              <li>
                Scrape, mirror, or otherwise copy the Site at scale without our written permission.
              </li>
            </ul>
          </Section>

          <Section title="7. Intellectual property">
            <p>
              The Site, including its design, text, logos, and software, is owned by Mobeen Center
              or its licensors and is protected by intellectual property law. You may view and share
              links to the Site, but you may not reproduce substantial portions of it for commercial
              purposes without our permission. Content you submit (such as a competition entry
              description) remains yours; by submitting it you grant us a non-exclusive license to
              display and archive it in connection with the event.
            </p>
          </Section>

          <Section title="8. Third-party services">
            <p>
              The Site relies on third-party services including Cloudflare (hosting) and Stripe
              (payment processing). Your use of those services is also governed by their respective
              terms and privacy notices. We are not responsible for the practices of third parties
              beyond our reasonable control.
            </p>
          </Section>

          <Section title="9. Disclaimer of warranties">
            <p>
              The Site is provided on an &ldquo;as is&rdquo; and &ldquo;as available&rdquo; basis.
              We do not warrant that the Site will be uninterrupted, error-free, or free from
              harmful components. To the maximum extent permitted by law, we disclaim all implied
              warranties, including merchantability, fitness for a particular purpose, and
              non-infringement.
            </p>
          </Section>

          <Section title="10. Limitation of liability">
            <p>
              To the maximum extent permitted by law, Mobeen Center and its officers, volunteers,
              and employees are not liable for indirect, incidental, special, consequential, or
              punitive damages arising from your use of the Site. Our total liability for any claim
              relating to the Site or a donation is limited to the amount you paid us in the twelve
              months before the claim arose, or one hundred dollars, whichever is greater. Some
              jurisdictions do not allow these limits; in those cases the limits apply to the
              fullest extent permitted.
            </p>
          </Section>

          <Section title="11. Governing law">
            <p>
              These Terms are governed by the laws of the State of California, without regard to its
              conflict-of-laws rules. The exclusive venue for any dispute arising out of or relating
              to these Terms is the state and federal courts located in California, and you consent
              to the personal jurisdiction of those courts.
            </p>
          </Section>

          <Section title="12. Changes">
            <p>
              We may update these Terms. Material changes will be announced on this page with a new
              effective date. Your continued use of the Site after the effective date constitutes
              acceptance of the updated Terms.
            </p>
          </Section>

          <Section title="13. Contact">
            <p>
              Questions about these Terms can be sent to <Mail />.
            </p>
          </Section>
        </Container>
      </main>
      <Footer />
    </>
  );
}

function Section({
  title,
  id,
  children,
}: {
  title: string;
  id?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="mt-10 scroll-mt-24" id={id}>
      <h2 className="font-display text-2xl font-light text-ink">{title}</h2>
      <div className="mt-3 space-y-3 text-pretty leading-relaxed text-muted">{children}</div>
    </section>
  );
}

function Mail() {
  return (
    <a href={`mailto:${CONTACT_EMAIL}`} className="text-accent underline-offset-4 hover:underline">
      {CONTACT_EMAIL}
    </a>
  );
}
